import * as React from 'react';
import { useMemo } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import type { State } from 'state';
import { getPortalId } from 'state/selectors/context';
import { getPortalI18nTranslations, getPortalLocale } from 'state/selectors/portal';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { getMessages, onIntlNextError } from '@atlassian/help-center-common-util/i18n-messages';
import { getLanguageFromLocale } from '@atlassian/help-center-common-util/locale-data';

const PORTAL_LANGUAGE_PACK_IN_MODEL = 'jsm-portal-include-language-pack-in-model';

const isPortalLanguagePackInModelEnabled = (): boolean =>
	FeatureGates.checkGate(PORTAL_LANGUAGE_PACK_IN_MODEL);

interface StateProps {
	i18nTranslations: Record<string, string> | undefined;
	portalLocale: string | undefined;
}

interface OwnProps {
	children: React.ReactNode;
}

type Props = StateProps & OwnProps;

export const PortalIntlBoundary: React.FC<Props> = ({
	i18nTranslations,
	portalLocale,
	children,
}) => {
	const outerIntl = useIntl();

	const shouldOverride =
		isPortalLanguagePackInModelEnabled() &&
		!!i18nTranslations &&
		Object.keys(i18nTranslations).length > 0;

	const messages = useMemo(
		() => (shouldOverride ? { ...getMessages(), ...i18nTranslations } : undefined),
		[shouldOverride, i18nTranslations],
	);

	if (!shouldOverride) {
		return <>{children}</>;
	}

	return (
		<IntlProvider
			locale={portalLocale ? getLanguageFromLocale(portalLocale) : outerIntl.locale}
			defaultLocale={outerIntl.defaultLocale}
			messages={messages}
			formats={outerIntl.formats}
			timeZone={outerIntl.timeZone}
			onError={onIntlNextError}
		>
			{children}
		</IntlProvider>
	);
};

const selector = createSelector(
	(state: State) => {
		const portalId = getPortalId(state);
		return {
			i18nTranslations: getPortalI18nTranslations(state, portalId),
			portalLocale: getPortalLocale(state, portalId),
		};
	},
	({ i18nTranslations, portalLocale }): StateProps => ({ i18nTranslations, portalLocale }),
);

export default connect(selector)(PortalIntlBoundary);
